/**
 * Calls the backend to mint a signed patient survey URL (optionally shortened).
 * @agentic connections
 */

import { getApiEndpoint, getApiAuthHeaders } from "@/utils/api/getApiEndpoint";

/** Successful response from POST /api/v1/surveys/generate-link. @agentic connections */
export type SurveyLinkApiResponse = {
  url: string;
  token: string;
};

/**
 * Requests a survey invite link for the given patient and questionnaire URLs.
 * Requires a Medplum access token (API route uses api key + Medplum JWT).
 * @agentic connections
 */
export const requestSurveyLink = async (params: {
  accessToken: string;
  patientId: string;
  questionnaireUrls: string[];
  signal?: AbortSignal;
}): Promise<SurveyLinkApiResponse> => {
  const apiUrl = getApiEndpoint("/api/v1/surveys/generate-link");

  const response = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Medplum-Authorization": `Bearer ${params.accessToken}`,
      ...getApiAuthHeaders(),
    },
    body: JSON.stringify({
      patientId: params.patientId,
      questionnaireUrls: params.questionnaireUrls,
    }),
    signal: params.signal,
  });

  const raw = await response.text();
  let body: Record<string, unknown> = {};
  try {
    body = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
  } catch {
    body = { raw };
  }

  if (!response.ok) {
    const message =
      (typeof body.error === "string" ? body.error : null) ||
      `Could not create survey link (${response.status})`;
    throw new Error(message);
  }

  const url = body.url;
  const token = body.token;
  if (typeof url !== "string" || typeof token !== "string") {
    throw new Error("Invalid response from survey link service");
  }

  return { url, token };
};
